import React, { useEffect, useState, useCallback } from "react";
import {
  LayoutGrid, Boxes, Wallet, ListChecks, FolderOpen, ShieldCheck,
  Search, Lock, CircleAlert, Building2, Cpu, ArrowLeft, RefreshCw, Plug, SlidersHorizontal, Save,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "taven_user_token";

const NAV = [
  { id: "workspace", label: "My Workspace", icon: LayoutGrid },
  { id: "twin", label: "Twin Center", icon: Building2 },
  { id: "catalog", label: "Service Catalog", icon: Boxes },
  { id: "tegob", label: "TEGOB (23 Depts)", icon: Cpu },
  { id: "connector", label: "Connector Hub", icon: Plug },
  { id: "billing", label: "Billing & Credits", icon: Wallet },
  { id: "jobs", label: "Jobs", icon: ListChecks },
  { id: "files", label: "Files & Reports", icon: FolderOpen },
  { id: "admin", label: "Admin · Pricing", icon: SlidersHorizontal, authorized: true },
  { id: "control", label: "Control Center", icon: ShieldCheck, authorized: true },
];

const stateColor = (st) => ({
  CONNECTED: { bg: "#12291a", fg: "#4ade80", bd: "#1f5a37" },
  NOT_CONNECTED: { bg: "#2a1a1a", fg: "#ff8080", bd: "#6a2a2a" },
  BLOCKED: { bg: "#2a1a1a", fg: "#ff6b6b", bd: "#6a2a2a" },
  NOT_PROVEN: { bg: "#3a2a12", fg: "#f5a623", bd: "#7a5410" },
}[st] || { bg: "#141b2a", fg: "#aeb6c6", bd: "#1c2333" });

const badgeColor = (label) =>
  label === "External Dependency" ? { bg: "#3a2a12", fg: "#f5a623", bd: "#7a5410" }
    : { bg: "#2a1a1a", fg: "#ff6b6b", bd: "#6a2a2a" };

async function getJSON(path, auth = false) {
  const headers = {};
  if (auth) {
    const t = localStorage.getItem(TOKEN_KEY);
    if (t) headers.Authorization = `Bearer ${t}`;
  }
  const r = await fetch(`${API}${path}`, { headers });
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json();
}

const Banner = ({ b }) => b ? (
  <div data-testid="governance-banner" style={{ background: "#1a1408", border: "1px solid #4a3a10", color: "#f5c451",
    padding: "10px 16px", borderRadius: 10, fontSize: 13, marginBottom: 20, display: "flex", gap: 10, alignItems: "center" }}>
    <CircleAlert size={16} /> <span><b>{b.pricing}</b> · {b.sellable} · OAQA {b.oaqa} · OMADA {b.omada} · Marketplace {b.marketplace}</span>
  </div>
) : null;

export default function YedaIOS() {
  const [active, setActive] = useState("catalog");
  const [summary, setSummary] = useState(null);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("");
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { getJSON("/api/yeda-ios/summary").then(setSummary).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true); setErr(null); setData(null); setSelected(null);
    try {
      if (active === "catalog") {
        const qs = new URLSearchParams({ ...(query ? { q: query } : {}), ...(cat ? { category: cat } : {}) }).toString();
        setData(await getJSON(`/api/yeda-ios/catalog${qs ? "?" + qs : ""}`));
      } else if (active === "connector") {
        setData(await getJSON("/api/yeda-ios/connector-hub"));
      } else if (active === "twin") {
        setData(await getJSON("/api/yeda-ios/twin-center"));
      } else if (active === "tegob") {
        setData(await getJSON("/api/yeda-ios/tegob"));
      } else if (active === "admin") {
        setData(await getJSON("/api/yeda-ios/admin/pricing", true));
      } else if (active === "control") {
        setData(await getJSON("/api/yeda-ios/control-center", true));
      } else {
        setData(await getJSON(`/api/yeda-ios/${active}`));
      }
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, [active, query, cat]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#0b0e14", color: "#e6e9ef",
      fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      {/* Sidebar */}
      <aside style={{ width: 250, background: "#0f1420", borderRight: "1px solid #1c2333", padding: "24px 14px",
        position: "sticky", top: 0, height: "100vh" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 8px 22px", borderBottom: "1px solid #1c2333", marginBottom: 16 }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: "linear-gradient(135deg,#5b8cff,#3ad1c6)",
            display: "grid", placeItems: "center", fontWeight: 800, color: "#04121a" }}>Y</div>
          <div><div style={{ fontWeight: 700, fontSize: 15 }}>YEDA iOS</div>
            <div style={{ fontSize: 11, color: "#6b7688" }}>Operator Console</div></div>
        </div>
        {NAV.map(({ id, label, icon: Icon, authorized }) => (
          <button key={id} data-testid={`nav-${id}`} onClick={() => { setActive(id); setData(null); setSelected(null); }}
            style={{ width: "100%", display: "flex", alignItems: "center", gap: 11, padding: "11px 12px", marginBottom: 4,
              background: active === id ? "#182238" : "transparent", color: active === id ? "#8db4ff" : "#aeb6c6",
              border: "none", borderRadius: 9, cursor: "pointer", fontSize: 14, textAlign: "left", fontWeight: active === id ? 600 : 500 }}>
            <Icon size={17} /> {label} {authorized && <Lock size={12} style={{ marginLeft: "auto", opacity: 0.6 }} />}
          </button>
        ))}
        {summary && (
          <div style={{ marginTop: 22, padding: "12px", background: "#0c101a", borderRadius: 10, border: "1px solid #1c2333", fontSize: 12, color: "#8791a3" }}>
            <div style={{ color: "#e6e9ef", fontWeight: 700, fontSize: 22 }} data-testid="service-count">{summary.reconciliation.total_reconciled}</div>
            services reconciled<div style={{ marginTop: 6, color: "#4ea3ff" }}>{summary.reconciliation.discrepancy_46_vs_47}</div>
            <div style={{ marginTop: 8, color: "#6b7688" }}>Candidate {summary.candidate_id.slice(-16)}</div>
          </div>
        )}
      </aside>

      {/* Main */}
      <main style={{ flex: 1, padding: "28px 34px", maxWidth: 1400 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, margin: "0 0 4px", letterSpacing: -0.5 }} data-testid="section-title">
          {NAV.find((n) => n.id === active)?.label}</h1>
        <div style={{ color: "#6b7688", fontSize: 13, marginBottom: 22 }}>YEDA iOS — governed, fail-closed <b>web operator console</b> (service-layer visibility · not the native iOS app)</div>
        {summary && <Banner b={summary.governance_banner} />}

        {loading && <div style={{ color: "#6b7688" }}><RefreshCw size={16} className="spin" /> Loading…</div>}
        {err && <div data-testid="error-box" style={{ color: "#ff8080", background: "#241414", padding: 16, borderRadius: 10, border: "1px solid #5a2a2a" }}>
          {active === "control" ? "🔒 Control Center requires an authorized (logged-in) user. Please sign in." : `Error: ${err}`}</div>}

        {active === "connector" && data && <ConnectorHubView d={data} />}
        {active === "twin" && data && <TwinCenterView d={data} />}
        {active === "tegob" && data && <TegobView d={data} />}
        {active === "admin" && data && <AdminPricingView d={data} reload={load} />}

        {/* CATALOG */}
        {active === "catalog" && data && !selected && (
          <>
            <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#0f1420", border: "1px solid #1c2333", borderRadius: 9, padding: "8px 12px", flex: 1, minWidth: 220 }}>
                <Search size={16} color="#6b7688" />
                <input data-testid="catalog-search" placeholder="Search by ID, name…" value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  style={{ background: "transparent", border: "none", color: "#e6e9ef", outline: "none", width: "100%", fontSize: 14 }} />
              </div>
              <select data-testid="catalog-category" value={cat} onChange={(e) => setCat(e.target.value)}
                style={{ background: "#0f1420", color: "#e6e9ef", border: "1px solid #1c2333", borderRadius: 9, padding: "8px 12px", fontSize: 14 }}>
                <option value="">All categories ({data.total})</option>
                {data.categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ color: "#6b7688", fontSize: 13, marginBottom: 12 }} data-testid="catalog-count">Showing {data.shown} of {data.total} services</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))", gap: 14 }}>
              {data.services.map((s) => {
                const bc = badgeColor(s.status_label);
                return (
                  <div key={s.id} data-testid={`service-card-${s.id}`} onClick={() => setSelected(s)}
                    style={{ background: "#0f1420", border: "1px solid #1c2333", borderRadius: 12, padding: 16, cursor: "pointer", transition: "border-color .2s" }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#3a4a6a")}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#1c2333")}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 8 }}>
                      <div>
                        <div style={{ fontSize: 11, color: "#5b8cff", fontWeight: 600, letterSpacing: 0.5 }}>{s.id}</div>
                        <div style={{ fontSize: 16, fontWeight: 700, margin: "3px 0" }}>{s.name}</div>
                      </div>
                      <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 20, background: bc.bg, color: bc.fg, border: `1px solid ${bc.bd}`, whiteSpace: "nowrap" }}>{s.status_label}</span>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "10px 0", fontSize: 11, color: "#8791a3" }}>
                      <span style={{ background: "#141b2a", padding: "3px 8px", borderRadius: 6 }}>{s.category}</span>
                      <span style={{ background: "#141b2a", padding: "3px 8px", borderRadius: 6 }}><Cpu size={11} style={{ verticalAlign: -1 }} /> {s.managed_by.internal_engine}</span>
                      <span style={{ background: "#141b2a", padding: "3px 8px", borderRadius: 6 }}>UI: {s.ui_surface}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#aeb6c6", borderTop: "1px solid #1c2333", paddingTop: 10 }}>
                      <span>{s.pricing.default_estimated_credits} credits</span>
                      <span style={{ color: "#f5c451" }}>~${s.pricing.default_estimated_price_usd} <span style={{ fontSize: 9, color: "#8791a3" }}>PENDING</span></span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* CATALOG DETAIL */}
        {active === "catalog" && selected && (
          <div data-testid="service-detail">
            <button onClick={() => setSelected(null)} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", color: "#5b8cff", cursor: "pointer", fontSize: 14, marginBottom: 16 }}><ArrowLeft size={16} /> Back to catalog</button>
            <div style={{ background: "#0f1420", border: "1px solid #1c2333", borderRadius: 14, padding: 26 }}>
              <div style={{ fontSize: 12, color: "#5b8cff", fontWeight: 600 }}>{selected.id} · {selected.category}</div>
              <h2 style={{ fontSize: 24, fontWeight: 800, margin: "4px 0 6px" }}>{selected.name}</h2>
              <p style={{ color: "#aeb6c6", fontSize: 14 }}>{selected.description}</p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 16, marginTop: 20 }}>
                <Detail title="Status"><b style={{ color: "#ff8f6b" }}>{selected.status_label}</b><br />{selected.status_reason}</Detail>
                <Detail title="Pricing (SNAPSHOT · PENDING)"><b style={{ color: "#f5c451" }}>~${selected.pricing.default_estimated_price_usd}</b> · {selected.pricing.default_estimated_credits} credits (base {selected.pricing.base_credits})<br /><span style={{ fontSize: 11 }}>{selected.pricing.pricing_note}</span></Detail>
                <Detail title="Credit Cost / Margin">Model: {selected.credit_cost.model}<br />Provider cost: {selected.credit_cost.provider_cost_model}<br />Margin: {selected.credit_cost.contribution_margin_status}</Detail>
                <Detail title="Entitlement">{selected.entitlement.billing_model} · Continuity: {selected.entitlement.continuity}<br />Read: {selected.entitlement.read_authority}</Detail>
                <Detail title="Inputs">Data: {selected.inputs.data_classification.join(", ") || "—"}<br />Requires: {selected.inputs.required_capabilities.join(", ") || "—"}<br />Config: {selected.inputs.unresolved_config.join(", ") || "—"}</Detail>
                <Detail title="Outputs">SoR: {selected.outputs.system_of_record}<br />Export: {selected.outputs.export_policy}</Detail>
                <Detail title="Limits">Scope: {selected.limits.execution_scope}<br />Write: {selected.limits.write_authority}<br />Cross-tenant: {selected.limits.cross_tenant}</Detail>
                <Detail title="Evidence">Ready: {String(selected.evidence.evidence_ready)} · Runtime: {String(selected.evidence.runtime_verified)}<br />Control-plane: {selected.evidence.control_plane}<br />API: {selected.evidence.api_e2e} · Browser: {selected.evidence.browser_e2e}</Detail>
                <Detail title="Managed By">{selected.managed_by.internal_engine}<br />Data owner: {selected.managed_by.data_owner}<br />{selected.managed_by.governance}</Detail>
              </div>
            </div>
          </div>
        )}

        {/* WORKSPACE / BILLING / JOBS / FILES / CONTROL */}
        {!["catalog", "connector", "admin", "twin", "tegob"].includes(active) && data && (
          <pre data-testid={`panel-${active}`} style={{ background: "#0f1420", border: "1px solid #1c2333", borderRadius: 12, padding: 20, fontSize: 13, color: "#c4ccda", overflow: "auto", lineHeight: 1.6 }}>
            {active === "billing" && <BillingView d={data} />}
            {active === "workspace" && <WorkspaceView d={data} />}
            {active === "jobs" && <EmptyView icon={ListChecks} title="No jobs yet" note={data.note} />}
            {active === "files" && <EmptyView icon={FolderOpen} title="No files or reports yet" note={data.note} />}
            {active === "control" && <ControlView d={data} />}
          </pre>
        )}
      </main>
      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

const Detail = ({ title, children }) => (
  <div style={{ background: "#0c101a", border: "1px solid #1c2333", borderRadius: 10, padding: 14 }}>
    <div style={{ fontSize: 11, color: "#6b7688", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>{title}</div>
    <div style={{ fontSize: 13, color: "#c4ccda", lineHeight: 1.6 }}>{children}</div>
  </div>
);

const Card = ({ children }) => <div style={{ background: "#0c101a", border: "1px solid #1c2333", borderRadius: 10, padding: 16, marginBottom: 12 }}>{children}</div>;

const BillingView = ({ d }) => (
  <div>
    <div style={{ color: "#f5c451", marginBottom: 14 }}>⚠️ {d.banner}</div>
    <Card><b>Membership (snapshot)</b> — {d.membership_snapshot.code}: ${d.membership_snapshot.monthly_price_usd}/mo · ${d.membership_snapshot.annual_price_usd}/yr<br /><span style={{ color: "#f5a623" }}>{d.membership_snapshot.status}</span></Card>
    <Card><b>Credits (snapshot)</b> — ${d.credit_snapshot.unit_price_usd}/unit · packs: {d.credit_snapshot.packs.map((p) => `${p.credits}=$${p.price_usd}`).join(", ")}<br /><span style={{ color: "#f5a623" }}>{d.credit_snapshot.status}</span></Card>
    <Card><b>Balance</b> — {d.balance.credits} credits · {d.balance.note}</Card>
    <Card><b>Financial rules</b> — Frozen {d.financial_rules.frozen}. {d.financial_rules.detail}</Card>
  </div>
);

const WorkspaceView = ({ d }) => (
  <div>
    <Card><b>{d.membership.name}</b> ({d.membership.code}) — {d.membership.status}</Card>
    <Card><b>Capabilities</b><br />{d.capabilities.join(" · ")}</Card>
    <Card><b>Projects</b> — {d.projects.length} · <b>Recent activity</b> — {d.recent_activity.length}<br /><span style={{ color: "#6b7688" }}>{d.note}</span></Card>
  </div>
);

const EmptyView = ({ icon: Icon, title, note }) => (
  <div style={{ textAlign: "center", padding: "40px 0", color: "#6b7688" }}>
    <Icon size={40} style={{ opacity: 0.4 }} /><div style={{ fontSize: 16, marginTop: 12, color: "#aeb6c6" }}>{title}</div>
    <div style={{ fontSize: 13, marginTop: 6 }}>{note}</div>
  </div>
);

const ControlView = ({ d }) => (
  <div>
    <div style={{ color: "#3ad1c6", marginBottom: 14 }}><ShieldCheck size={16} style={{ verticalAlign: -3 }} /> Authorized · Verdict: {d.governance.verdict_label}</div>
    <Card><b>Candidate</b> {d.candidate_id}<br />Gate Ledger v{d.gate_ledger_version}</Card>
    <Card><b>Governance</b> — OAQA {d.governance.oaqa} · OMADA {d.governance.omada} · Production {String(d.governance.production_authorized)} · Marketplace {d.governance.marketplace}</Card>
    <Card><b>Blockers</b><br />{Object.entries(d.blockers).map(([k, v]) => <div key={k}>{k}: <b>{String(v)}</b></div>)}</Card>
    <Card><b>Reconciliation</b> — {d.reconciliation.total_reconciled} services · {d.reconciliation.discrepancy_46_vs_47}</Card>
    <div style={{ color: "#6b7688" }}>{d.note}</div>
  </div>
);

const StateBadge = ({ st }) => {
  const c = stateColor(st);
  return <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 20, background: c.bg, color: c.fg, border: `1px solid ${c.bd}`, whiteSpace: "nowrap" }}>{st}</span>;
};

const ConnectorHubView = ({ d }) => (
  <div data-testid="panel-connector">
    <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
      {[["Connectors", `${d.summary.connectors.NOT_PROVEN} NOT_PROVEN / ${d.summary.connectors.total}`],
        ["Providers", `${d.summary.providers.NOT_CONNECTED} NOT_CONNECTED / ${d.summary.providers.total}`],
        ["External services", `${d.summary.external_dependency_services} BLOCKED`]].map(([k, v]) => (
        <div key={k} style={{ background: "#0f1420", border: "1px solid #1c2333", borderRadius: 10, padding: "12px 16px", flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 11, color: "#6b7688", textTransform: "uppercase" }}>{k}</div>
          <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>{v}</div></div>))}
    </div>
    <SectionTitle>Connectors</SectionTitle>
    {d.connectors.map((c) => (
      <div key={c.connector_id} data-testid={`connector-${c.connector_id}`} style={rowStyle}>
        <div><b>{c.connector_id}</b> <span style={{ color: "#6b7688" }}>· {c.capability}</span><div style={{ fontSize: 12, color: "#8791a3" }}>{c.reason}</div></div>
        <StateBadge st={c.state} /></div>))}
    <SectionTitle>Providers</SectionTitle>
    {d.providers.map((p) => (
      <div key={p.provider_record_id} style={rowStyle}>
        <div><b>{p.provider_record_id}</b> <span style={{ color: "#6b7688" }}>· {p.capability}</span><div style={{ fontSize: 12, color: "#8791a3" }}>Candidates: {(p.candidates || []).join(", ") || "—"} · {p.reason}</div></div>
        <StateBadge st={p.state} /></div>))}
    <SectionTitle>External-dependency services ({d.external_services.length})</SectionTitle>
    {d.external_services.map((s) => (
      <div key={s.service_id} style={rowStyle}>
        <div><b>{s.service_id}</b> · {s.name}<div style={{ fontSize: 12, color: "#8791a3" }}>{s.reason}</div></div>
        <StateBadge st={s.state} /></div>))}
  </div>
);

const AdminPricingView = ({ d, reload }) => {
  const [edit, setEdit] = useState({});
  const [saving, setSaving] = useState(null);
  const save = async (sid) => {
    setSaving(sid);
    const t = localStorage.getItem(TOKEN_KEY);
    const body = edit[sid] || {};
    await fetch(`${API}/api/yeda-ios/admin/pricing/${sid}`, {
      method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}` },
      body: JSON.stringify({ price_usd: body.price_usd != null ? parseFloat(body.price_usd) : undefined,
        available: body.available, reason: body.reason || "admin update" }),
    });
    setSaving(null); setEdit((e) => ({ ...e, [sid]: undefined })); reload();
  };
  return (
    <div data-testid="panel-admin">
      <div style={{ background: "#1a1408", border: "1px solid #4a3a10", color: "#f5c451", padding: "10px 16px", borderRadius: 10, fontSize: 13, marginBottom: 16 }}>
        <CircleAlert size={14} style={{ verticalAlign: -2 }} /> {d.note}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr style={{ color: "#6b7688", textAlign: "left" }}>
            {["Service", "Snapshot $", "Admin $", "Available", "Status", "Updated by", ""].map((h) => <th key={h} style={{ padding: "8px 10px", borderBottom: "1px solid #1c2333" }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {d.rows.map((r) => (
              <tr key={r.service_id} data-testid={`pricing-row-${r.service_id}`} style={{ borderBottom: "1px solid #141b2a" }}>
                <td style={td}><b style={{ color: "#8db4ff" }}>{r.service_id}</b><div style={{ color: "#8791a3", fontSize: 12 }}>{r.name}</div></td>
                <td style={td}>${r.snapshot_price_usd}</td>
                <td style={td}><input data-testid={`price-input-${r.service_id}`} type="number" step="0.01" defaultValue={r.admin_price_usd ?? ""}
                  onChange={(e) => setEdit((x) => ({ ...x, [r.service_id]: { ...x[r.service_id], price_usd: e.target.value } }))}
                  style={{ width: 80, background: "#0c101a", border: "1px solid #1c2333", color: "#e6e9ef", borderRadius: 6, padding: "5px 7px" }} /></td>
                <td style={td}><input type="checkbox" data-testid={`avail-${r.service_id}`} defaultChecked={!!r.available}
                  onChange={(e) => setEdit((x) => ({ ...x, [r.service_id]: { ...x[r.service_id], available: e.target.checked } }))} /></td>
                <td style={td}><span style={{ color: "#f5a623", fontSize: 11 }}>{r.price_status}</span></td>
                <td style={{ ...td, color: "#8791a3", fontSize: 12 }}>{r.updated_by || "—"}</td>
                <td style={td}><button data-testid={`save-${r.service_id}`} onClick={() => save(r.service_id)} disabled={saving === r.service_id}
                  style={{ display: "flex", alignItems: "center", gap: 5, background: "#182238", color: "#8db4ff", border: "1px solid #2a3a5a", borderRadius: 7, padding: "6px 10px", cursor: "pointer", fontSize: 12 }}>
                  <Save size={13} /> {saving === r.service_id ? "…" : "Save"}</button></td>
              </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SectionTitle = ({ children }) => <div style={{ fontSize: 13, color: "#6b7688", textTransform: "uppercase", letterSpacing: 0.6, margin: "18px 0 8px" }}>{children}</div>;
const rowStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, background: "#0f1420", border: "1px solid #1c2333", borderRadius: 10, padding: "12px 16px", marginBottom: 8 };
const td = { padding: "10px" };

const TwinCenterView = ({ d }) => (
  <div data-testid="panel-twin">
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 14, marginBottom: 16 }}>
      <div style={rowStyle}><div><b>Candidate</b><div style={{ fontSize: 12, color: "#8db4ff" }}>{d.candidate_identity.candidate_id}</div><div style={{ fontSize: 11, color: "#8791a3" }}>tree {d.candidate_identity.tree_digest_sha256.slice(0, 24)}…</div></div></div>
      <div style={rowStyle}><div><b>Gate Ledger</b><div style={{ fontSize: 12, color: "#aeb6c6" }}>v{d.gate_ledger.version}</div><div style={{ fontSize: 11, color: "#f5a623" }}>{d.gate_ledger.verdict_label}</div></div></div>
    </div>
    <SectionTitle>Live subsystem status</SectionTitle>
    {d.live_subsystem_status.map((s) => (
      <div key={s.name} style={rowStyle}><div><b>{s.name}</b><div style={{ fontSize: 12, color: "#8791a3" }}>{s.evidence}</div></div><StateBadge st={s.state === "ENGINEERING_VERIFIED" ? "CONNECTED" : s.state === "NOT_PROVEN" ? "NOT_PROVEN" : "NOT_CONNECTED"} /></div>))}
    <SectionTitle>Lineage</SectionTitle>
    <div style={rowStyle}><div style={{ fontSize: 12, color: "#aeb6c6" }}>parent: {d.lineage.parent}<br />grandparent: {d.lineage.grandparent}<br />native iOS: {d.system_map.native_ios_app_source}</div></div>
    <SectionTitle>Governance & blockers</SectionTitle>
    <div style={rowStyle}><div style={{ fontSize: 13 }}>OAQA <b style={{ color: "#f5a623" }}>{d.governance_status.oaqa}</b> · OMADA <b style={{ color: "#ff6b6b" }}>{d.governance_status.omada}</b> · Production <b>{String(d.governance_status.production_authorized)}</b></div></div>
    <div style={rowStyle}><div style={{ fontSize: 12, color: "#8791a3" }}>{Object.entries(d.blockers).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div></div>
    <div style={{ fontSize: 12, color: "#6b7688", marginTop: 10 }}>{d.note}</div>
  </div>
);

const TegobView = ({ d }) => (
  <div data-testid="panel-tegob">
    <div style={{ background: "#0f1420", border: "1px solid #1c2333", borderRadius: 10, padding: "12px 16px", marginBottom: 14 }}>
      <b>{d.total}/{d.expected} departments</b> reconciled · <span style={{ color: "#f5a623" }}>{d.automation_truth}</span>
      <div style={{ fontSize: 12, color: "#8791a3", marginTop: 4 }}>{d.governance_note}</div>
    </div>
    {d.departments.map((o) => (
      <div key={o.office_id} data-testid={`tegob-${o.office_id}`} style={rowStyle}>
        <div style={{ flex: 1 }}><b style={{ color: "#8db4ff" }}>{o.office_id}</b> · {o.title}
          <div style={{ fontSize: 12, color: "#8791a3" }}>{o.responsibility}</div>
          <div style={{ fontSize: 11, color: "#6b7688", marginTop: 3 }}>{o.agent_capability} · {o.functional_proven ? "✅ chat proven" : "chat"} · fail: {o.failure_behavior}</div>
        </div>
        <StateBadge st={o.automation_truth_state === "PARTIALLY_AUTOMATED" ? "NOT_PROVEN" : o.automation_truth_state} /></div>))}
  </div>
);
